import { ManagedUserRolesType } from "@redotech/account-common/roles";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { supportViewFolderSchema } from "./support-folder-parser";
import { FolderData } from "./support/folder-types";
import {
  UserJob,
  UserJobType,
  UserLevel,
  UserLevelType,
} from "./user/job-level";
import {
  UserNotificationSettingsZodSchema,
  type UserNotificationSettings,
} from "./user/user-notification-settings";

export const USER_ROLES = [
  "admin",
  "managed-claims-admin",
  "support",
  "user",
  "reply-only",
  "view-only",
  "audien-support",
] as const;
export type UserRole = (typeof USER_ROLES)[number];
export const UserRole = USER_ROLES;

export const SupportV2ViewOrderingType = {
  VIEW: "view",
  FOLDER: "folder",
} as const;
export type SupportV2ViewOrderingType =
  (typeof SupportV2ViewOrderingType)[keyof typeof SupportV2ViewOrderingType];

export const GetUserZodSchema = z.object({
  roles: z.array(createEnum(USER_ROLES, { enumName: "UserRole" })).nullish(),
  _id: zExt.objectId(),
  email: z.string(),
  createdAt: z.date(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  name: z.string().nullish(),
  updatedAt: z.date(),
  image: z.array(z.any()).nullish(),
  team: zExt.objectId().nullish(),
  permissions: z.array(z.any()).nullish(),
  notifications: UserNotificationSettingsZodSchema,
  customViewOrdering: z
    .array(
      z.object({
        viewName: z.string().optional(),
        view: zExt.objectId().optional(),
      }),
    )
    .optional(),
  supportV2ViewOrdering: z
    .array(z.object({ id: z.string(), type: z.enum(["view", "folder"]) }))
    .optional(),
  emailSignature: z.string().optional(),
  usePersonalSignature: z.boolean().optional(),
  viewFolders: z.array(supportViewFolderSchema).optional(),
  preferredLocale: z.string().nullish(),
  jobProfile: z
    .object({
      job: z.array(createEnum(UserJob, { enumName: "UserJob" })).optional(),
      level: createEnum(UserLevel, { enumName: "UserLevel" }).optional(),
    })
    .optional(),
});
export interface GetUser {
  /**
   * @deprecated this will no longer be populated.
   * Check permissions, not roles. (useHasPermission in frontend)
   */
  roles?: string[];
  userRoles: string[];
  managedUserRoles: ManagedUserRolesType[];
  _id: string;
  email: string;
  createdAt: string;
  firstName: string;
  lastName: string;
  name: string;
  updatedAt: string;
  image: any[];
  team?: string;
  notifications: UserNotificationSettings;
  customViewOrdering?: {
    viewName?: string; // View name for default views
    view?: string; // View _id for custom views
  }[];
  supportV2ViewOrdering?: { id: string; type: "view" | "folder" }[];
  emailSignature?: string;
  usePersonalSignature?: boolean;
  viewFolders?: FolderData[];
  preferredLocale?: string;
  jobProfile?: { job?: UserJobType[]; level?: UserLevelType };
}

export enum UserCreationSource {
  UNKNOWN = "unknown",
  REDO_ADMIN = "redo_admin",
  SHOPIFY_LOGIN = "shopify_login",
  ADDED_BY_MERCHANT = "added_by_merchant",
  BIGCOMMERCE_LOGIN = "bigcommerce_login",
  INTEGRATION_USER = "integration_user",
  POS_USER = "pos_user",
}
